import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Users, Loader2, Megaphone, Receipt, Plus, KeyRound, Trash2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const INR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

export default function ClientsList() {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      api.get('/users?role=client').then((r) => r.data).catch(() => []),
      api.get('/campaigns').then((r) => r.data).catch(() => []),
      api.get('/billing/overview').then((r) => r.data).catch(() => ({ items: [], total_outstanding: 0 })),
    ]).then(([clients, campaigns, billing]) => setData({ clients, campaigns, billing }));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const { clients, campaigns, billing } = data;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><p className="overline text-primary flex items-center gap-2"><Users className="h-3.5 w-3.5" /> Clients</p><h1 className="font-head text-3xl font-bold">Client Config</h1></div>
        <NewClientDialog onCreated={load} />
      </div>

      <div className="rounded-xl border border-border bg-card p-4" data-testid="clients-billing-summary">
        <p className="overline mb-1 flex items-center gap-2 text-muted-foreground"><Receipt className="h-3.5 w-3.5" /> Billing (admin only)</p>
        <p className="font-head text-2xl font-extrabold text-amber-400">{INR(billing.total_outstanding)}</p>
        <p className="text-xs text-muted-foreground">total outstanding · {billing.overdue_count || 0} overdue society(ies)</p>
        <div className="mt-2 space-y-1">
          {(billing.items || []).map((it) => (
            <Link key={it.society_id} to={`/societies/${it.society_id}`} className="flex items-center justify-between text-xs hover:text-primary">
              <span>{it.society_name}</span><span className={it.overdue ? 'text-rose-400' : 'text-muted-foreground'}>{INR(it.outstanding)}</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {clients.map((cl) => {
          const camps = campaigns.filter((c) => c.client_user_id === cl.id || c.client_name === cl.name);
          const sites = camps.reduce((a, c) => a + (c.site_ids?.length || 0), 0);
          return (
            <div key={cl.id} data-testid={`client-card-${cl.id}`} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary"><Users className="h-5 w-5" /></div>
                  <div><p className="font-head text-lg font-bold">{cl.name}</p><p className="text-xs text-muted-foreground">{cl.email}</p></div>
                </div>
                <div className="flex gap-1">
                  <ResetPwDialog client={cl} />
                  <DeleteClientButton client={cl} onDone={load} />
                </div>
              </div>
              <div className="mt-3 flex gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Megaphone className="h-3.5 w-3.5" /> {camps.length} campaigns</span>
                <span>{sites} sites</span>
              </div>
              <Link to={`/clients/${cl.id}`} data-testid={`client-detail-card-${cl.id}`} className="mt-3 flex items-center justify-center gap-1 rounded-lg border border-primary/40 py-2 text-sm font-semibold text-primary transition hover:bg-primary/10">
                View client details <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          );
        })}
        {!clients.length && <p className="text-sm text-muted-foreground">No client accounts yet — create one above.</p>}
      </div>
    </div>
  );
}

function NewClientDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error('Name, email and password are required');
    setSaving(true);
    try {
      await api.post('/auth/admin/users', { ...form, role: 'client' });
      toast.success('Client account created');
      setOpen(false); setForm({ name: '', email: '', password: '' }); onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Failed to create'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-client-btn"><Plus className="mr-1 h-4 w-4" /> New Client</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head text-2xl">New Client Login</DialogTitle><DialogDescription>Create a client account. Share the email + password so they can log in and view their campaigns.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <div><Label>Name</Label><Input data-testid="client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Email</Label><Input type="email" data-testid="client-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Temporary password</Label><Input data-testid="client-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={create} disabled={saving} data-testid="save-client-btn">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create Account</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPwDialog({ client }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  const reset = async () => {
    if (pw.length < 4) return toast.error('Password too short');
    setSaving(true);
    try { await api.post(`/auth/admin/users/${client.id}/reset-password`, { password: pw }); toast.success('Password reset'); setOpen(false); setPw(''); }
    catch { toast.error('Failed'); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`reset-pw-${client.id}`} title="Reset password"><KeyRound className="h-4 w-4" /></Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head text-xl">Reset password — {client.name}</DialogTitle><DialogDescription>Set a new password for this client login.</DialogDescription></DialogHeader>
        <Input data-testid={`reset-pw-input-${client.id}`} placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} />
        <DialogFooter><Button onClick={reset} disabled={saving} data-testid={`reset-pw-save-${client.id}`}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Reset</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteClientButton({ client, onDone }) {
  const del = async () => {
    if (!window.confirm(`Delete client account ${client.name}? Their campaigns remain but lose the linked login.`)) return;
    try { await api.delete(`/auth/admin/users/${client.id}`); toast.success('Client deleted'); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Failed'); }
  };
  return <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-400" data-testid={`delete-client-${client.id}`} onClick={del} title="Delete"><Trash2 className="h-4 w-4" /></Button>;
}
